import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Message } from 'primereact/message';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { Icon } from '@iconify/react';
import { axiosInstance } from '../../App';
import HistoricalValuesPanel from './HistoricalValuesPanel';
import './PatientClinicalHistoryModal.scss';

export type ClinicalHistoryAction =
  | { type: 'copyRecipe'; healthPatientServiceId: number; professionalId: number | null }
  | { type: 'copyStudyOrder'; healthPatientServiceId: number; professionalId: number | null }
  | { type: 'newRecipe'; professionalId: number | null }
  | { type: 'newStudyOrder'; professionalId: number | null }
  | { type: 'newMedicalIndication'; professionalId: number | null }
  | { type: 'loadStudyResult'; healthPatientServiceId: number; studyOrderId?: number; professionalId: number | null; professionalName?: string | null }
  | { type: 'odontogram'; professionalId: number | null; patientControlId?: number | null }
  | { type: 'editEvolution'; patientControlId: number; healthPatientServiceId: number; professionalId: number | null }
  | { type: 'viewEditLog'; patientControlId: number; professionalId: number | null };

export interface PatientClinicalHistoryViewProps {
  patientId: number | null;
  onAction?: (action: ClinicalHistoryAction) => void;
  /** Increment to force a data reload */
  refreshToken?: number;
}

interface ClinicalHistoryItem {
  healthPatientServiceId: number | null;
  patientControlId: number | null;
  serviceTypeId: number;
  serviceDate: string | null;
  serviceEnd: string | null;
  activity: string | null;
  responsible: string | null;
  reason: string | null;
  copyAvailable?: boolean;
  downloadAvailable?: boolean;
  editAvailable?: boolean;
}

interface ClinicalHistoryPage {
  data: ClinicalHistoryItem[];
  totalCount: number;
  offset: number;
  pageSize: number;
  professionalId: number | null;
  professionalName: string | null;
}

const dateTimeFormatter = new Intl.DateTimeFormat('es-AR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

interface ServiceTypePresentation {
  label: string;
  icon: string;
  tone: string;
}

const SERVICE_TYPE_PRESENTATIONS: Record<number, ServiceTypePresentation> = {
  1: { label: 'Internación', icon: 'pi-building', tone: 'amber' },
  2: { label: 'Consulta ambulatoria', icon: 'pi-user', tone: 'teal' },
  3: { label: 'Estudio', icon: 'pi-chart-bar', tone: 'blue' },
  4: { label: 'Guardia', icon: 'pi-heart', tone: 'slate' },
  8: { label: 'Cirugía', icon: 'pi-plus-circle', tone: 'violet' },
  11: { label: 'Evolución', icon: 'pi-file-edit', tone: 'cyan' },
  12: { label: 'Receta digital', icon: 'pi-file', tone: 'blue' },
  13: { label: 'Enfermería', icon: 'pi-heart-fill', tone: 'teal' },
  16: { label: 'Indicación médica', icon: 'pi-list', tone: 'cyan' },
  17: { label: 'Orden de estudio', icon: 'pi-clipboard', tone: 'blue' },
};

const DEFAULT_SERVICE_TYPE: ServiceTypePresentation = {
  label: 'Registro clínico',
  icon: 'pi-folder',
  tone: 'slate',
};

const SERVICE_TYPE_OPTIONS = Object.entries(SERVICE_TYPE_PRESENTATIONS).map(([id, p]) => ({
  label: p.label,
  value: Number(id),
  icon: p.icon,
  tone: p.tone,
}));

const formatDateTime = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTimeFormatter.format(date);
};

const serviceTypePresentation = (serviceTypeId: number): ServiceTypePresentation =>
  SERVICE_TYPE_PRESENTATIONS[serviceTypeId] ?? DEFAULT_SERVICE_TYPE;

const dateBody = (item: ClinicalHistoryItem) => (
  <div className="clinical-history-date">
    <span>{formatDateTime(item.serviceDate)}</span>
    {item.serviceEnd ? <small>Alta: {formatDateTime(item.serviceEnd)}</small> : null}
  </div>
);

const attentionBody = (item: ClinicalHistoryItem) => {
  const presentation = serviceTypePresentation(item.serviceTypeId);
  return (
    <div className="clinical-history-attention">
      <span className={`clinical-history-type clinical-history-type--${presentation.tone}`}>
        <i className={`pi ${presentation.icon}`} aria-hidden="true" />
        {presentation.label}
      </span>
      {item.activity && item.activity !== presentation.label ? (
        <span className="clinical-history-attention__activity">{item.activity}</span>
      ) : null}
    </div>
  );
};

const responsibleBody = (item: ClinicalHistoryItem) => (
  <div className="clinical-history-responsible">
    <span className="clinical-history-responsible__icon" aria-hidden="true">
      <i className="pi pi-user" />
    </span>
    <span>{item.responsible || 'Sin responsable informado'}</span>
  </div>
);

const reasonBody = (item: ClinicalHistoryItem) => (
  <span className={`clinical-history-reason${item.reason ? '' : ' clinical-history-reason--empty'}`}>
    {item.reason || 'Sin detalle registrado'}
  </span>
);

const getErrorMessage = (error: unknown): string => {
  const candidate = error as { response?: { data?: { message?: string } }; message?: string };
  return candidate.response?.data?.message
    || candidate.message
    || 'No se pudo cargar la historia clínica.';
};

export default function PatientClinicalHistoryView({
  patientId,
  onAction,
  refreshToken,
}: PatientClinicalHistoryViewProps) {
  const PAGE_SIZE = 25;
  const toast = useRef<Toast>(null);
  const gridShellRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<ClinicalHistoryItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [professionalId, setProfessionalId] = useState<number | null>(null);
  const [professionalName, setProfessionalName] = useState<string | null>(null);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const hasMore = items.length < totalCount;

  const fetchPage = useCallback((offset: number, append: boolean) => {
    if (patientId === null) return;
    let active = true;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    const params: Record<string, unknown> = { offset, pageSize: PAGE_SIZE };
    if (selectedServiceTypes.length > 0) {
      params.serviceTypeIds = selectedServiceTypes.join(',');
    }

    axiosInstance.get<ClinicalHistoryPage>(`/api/secured/patients/${patientId}/clinical-history`, {
      params,
    })
      .then((response) => {
        if (!active) return;
        const page = response.data.data ?? [];
        setItems((prev) => append ? [...prev, ...page] : page);
        setTotalCount(response.data.totalCount ?? 0);
        setProfessionalId(response.data.professionalId ?? null);
        setProfessionalName(response.data.professionalName ?? null);
      })
      .catch((requestError) => {
        if (!active) return;
        if (!append) {
          setItems([]);
          setTotalCount(0);
          setProfessionalId(null);
          setProfessionalName(null);
        }
        setError(getErrorMessage(requestError));
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
        setLoadingMore(false);
      });

    return () => { active = false; };
  }, [patientId, selectedServiceTypes]);

  useEffect(() => {
    setItems([]);
    setTotalCount(0);
    const cleanup = fetchPage(0, false);
    return cleanup;
  }, [patientId, reloadToken, refreshToken, selectedServiceTypes, fetchPage]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || loading || !hasMore) return;
    fetchPage(items.length, true);
  }, [items.length, loadingMore, loading, hasMore, fetchPage]);

  useEffect(() => {
    const shell = gridShellRef.current;
    if (!shell) return;
    const scrollEl = shell.querySelector('.p-datatable-wrapper') as HTMLElement | null;
    if (!scrollEl) return;

    const onScroll = () => {
      if (scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight < 150) {
        handleLoadMore();
      }
    };
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, [handleLoadMore]);

  const handleReload = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  const handleServiceTypeFilter = useCallback((e: MultiSelectChangeEvent) => {
    setSelectedServiceTypes(e.value as number[]);
  }, []);

  const serviceTypeItemTemplate = useCallback((option: typeof SERVICE_TYPE_OPTIONS[number]) => (
    <span className={`clinical-history-type clinical-history-type--${option.tone}`}>
      <i className={`pi ${option.icon}`} aria-hidden="true" />
      {option.label}
    </span>
  ), []);

  const handleCopy = useCallback((item: ClinicalHistoryItem) => {
    if (patientId === null || item.healthPatientServiceId == null) return;

    if (onAction) {
      onAction({
        type: item.serviceTypeId === 12 ? 'copyRecipe' : 'copyStudyOrder',
        healthPatientServiceId: item.healthPatientServiceId,
        professionalId,
      });
      return;
    }

    const params = new URLSearchParams({
      medereentityID: String(patientId),
    });
    let path: string;

    if (item.serviceTypeId === 12) {
      params.set('digitalRpId', String(item.healthPatientServiceId));
      path = '/recipeView';
    } else if (item.serviceTypeId === 17) {
      params.set('mode', 'COPY_STUDY_ORDER');
      params.set('copyFromHpsId', String(item.healthPatientServiceId));
      if (professionalId !== null) params.set('professionalId', String(professionalId));
      path = '/studyorderview';
    } else {
      return;
    }

    window.open(`${path}?${params.toString()}`, '_blank', 'noopener,noreferrer');
  }, [patientId, professionalId, onAction]);

  const handleDownload = useCallback(async (item: ClinicalHistoryItem) => {
    if (patientId === null || item.healthPatientServiceId == null) return;
    setDownloadingId(item.healthPatientServiceId);
    try {
      const response = await axiosInstance.get<Blob>(
        `/api/secured/patients/${patientId}/clinical-history/${item.healthPatientServiceId}/document`,
        { responseType: 'blob' },
      );
      const contentDisposition = String(response.headers['content-disposition'] ?? '');
      const encodedFilename = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
      const plainFilename = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1];
      const fallbackFilename = item.serviceTypeId === 12
        ? `Receta-${item.healthPatientServiceId}.pdf`
        : item.serviceTypeId === 3
        ? `Resultado-estudio-${item.healthPatientServiceId}.pdf`
        : item.serviceTypeId === 16
        ? `Indicacion-medica-${item.healthPatientServiceId}.pdf`
        : `Pedido-estudio-${item.healthPatientServiceId}.pdf`;
      const filename = encodedFilename
        ? decodeURIComponent(encodedFilename)
        : plainFilename ?? fallbackFilename;
      const documentUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = documentUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(documentUrl), 0);
    } catch (requestError) {
      toast.current?.show({
        severity: 'error',
        summary: 'No se pudo descargar el documento',
        detail: getErrorMessage(requestError),
        life: 4500,
      });
    } finally {
      setDownloadingId(null);
    }
  }, [patientId]);

  const actionsBody = useCallback((item: ClinicalHistoryItem) => {
    const itemId = item.healthPatientServiceId;
    const supportsActions = itemId != null
      && (item.serviceTypeId === 12 || item.serviceTypeId === 17);
    const copyAvailable = item.copyAvailable ?? supportsActions;
    const isStudyResult = item.serviceTypeId === 3;
    const downloadAvailable = item.downloadAvailable ?? (supportsActions || isStudyResult);
    const canLoadResult = itemId != null && item.serviceTypeId === 17;
    const canOpenOdontogram = item.patientControlId != null
      && item.activity === 'Odontología'
      && Boolean(onAction);
    const canEdit = item.editAvailable && item.patientControlId != null && itemId != null && Boolean(onAction);
    if (!copyAvailable && !downloadAvailable && !canLoadResult && !canOpenOdontogram && !canEdit) return null;

    return (
      <div className="clinical-history-actions">
        {canOpenOdontogram ? (
          <Button
            icon="pi pi-eye"
            className="p-button-text p-button-rounded clinical-history-actions__button"
            aria-label="Ver odontograma"
            tooltip="Ver odontograma"
            tooltipOptions={{ position: 'top' }}
            onClick={() => onAction?.({
              type: 'odontogram',
              professionalId,
              patientControlId: item.patientControlId,
            })}
          />
        ) : null}
        {canEdit ? (
          <>
            <Button
              icon="pi pi-pencil"
              className="p-button-text p-button-rounded clinical-history-actions__button"
              aria-label="Editar evolución"
              tooltip="Editar"
              tooltipOptions={{ position: 'top' }}
              onClick={() => onAction!({
                type: 'editEvolution',
                patientControlId: item.patientControlId!,
                healthPatientServiceId: itemId!,
                professionalId,
              })}
            />
            <Button
              icon="pi pi-history"
              className="p-button-text p-button-rounded clinical-history-actions__button"
              aria-label="Ver historial de ediciones"
              tooltip="Historial"
              tooltipOptions={{ position: 'top' }}
              onClick={() => onAction!({
                type: 'viewEditLog',
                patientControlId: item.patientControlId!,
                professionalId,
              })}
            />
          </>
        ) : null}
        {canLoadResult && onAction ? (
          <Button
            icon="pi pi-file-edit"
            className="p-button-text p-button-rounded clinical-history-actions__button"
            aria-label="Cargar resultado"
            tooltip="Cargar resultado"
            tooltipOptions={{ position: 'top' }}
            onClick={() => onAction({ type: 'loadStudyResult', healthPatientServiceId: 0, studyOrderId: itemId!, professionalId, professionalName })}
          />
        ) : null}
        {copyAvailable ? (
          <Button
            icon="pi pi-copy"
            className="p-button-text p-button-rounded clinical-history-actions__button"
            aria-label={item.serviceTypeId === 12 ? 'Copiar receta' : 'Copiar pedido de estudio'}
            tooltip={item.serviceTypeId === 12 ? 'Copiar receta' : 'Copiar pedido'}
            tooltipOptions={{ position: 'top' }}
            onClick={() => handleCopy(item)}
          />
        ) : null}
        {downloadAvailable ? (
          <Button
            icon="pi pi-download"
            className="p-button-text p-button-rounded clinical-history-actions__button"
            aria-label={item.serviceTypeId === 12 ? 'Descargar receta' : item.serviceTypeId === 3 ? 'Descargar resultado' : item.serviceTypeId === 16 ? 'Descargar indicación' : 'Descargar pedido de estudio'}
            tooltip={item.serviceTypeId === 12 ? 'Descargar receta' : item.serviceTypeId === 3 ? 'Descargar resultado' : item.serviceTypeId === 16 ? 'Descargar indicación' : 'Descargar pedido'}
            tooltipOptions={{ position: 'top' }}
            onClick={() => handleDownload(item)}
            loading={itemId != null && downloadingId === itemId}
            disabled={downloadingId !== null}
          />
        ) : null}
      </div>
    );
  }, [downloadingId, handleCopy, handleDownload, onAction, professionalId]);

  return (
    <div className="clinical-history-container">
    <section className="patient-clinical-history-view" aria-label="Historia clínica del paciente">
      <Toast ref={toast} />
      {error ? (
        <div className="clinical-history-error">
          <Message severity="error" text={error} />
          <Button label="Reintentar" icon="pi pi-refresh" className="p-button-text" onClick={handleReload} />
        </div>
      ) : null}

      {onAction ? (
        <div className="clinical-history-new-actions">
          <Button
            className="p-button-outlined clinical-history-new-actions__button"
            onClick={() => onAction({ type: 'newRecipe', professionalId })}
          >
            <Icon icon="mdi:pill" style={{ fontSize: '1rem' }} />
            <span>Nueva receta</span>
          </Button>
          <Button
            className="p-button-outlined clinical-history-new-actions__button"
            onClick={() => onAction({ type: 'newStudyOrder', professionalId })}
          >
            <Icon icon="mdi:flask-outline" style={{ fontSize: '1rem' }} />
            <span>Nuevo estudio</span>
          </Button>
          <Button
            className="p-button-outlined clinical-history-new-actions__button"
            onClick={() => onAction({ type: 'newMedicalIndication', professionalId })}
          >
            <Icon icon="mdi:file-document-edit-outline" style={{ fontSize: '1rem' }} />
            <span>Nueva indicación</span>
          </Button>
          <Button
            className="p-button-outlined clinical-history-new-actions__button"
            onClick={() => onAction({ type: 'loadStudyResult', healthPatientServiceId: 0, professionalId, professionalName })}
          >
            <Icon icon="mdi:clipboard-check-outline" style={{ fontSize: '1rem' }} />
            <span>Cargar resultado</span>
          </Button>
          <Button
            className="p-button-outlined clinical-history-new-actions__button"
            onClick={() => onAction({ type: 'odontogram', professionalId })}
          >
            <Icon icon="mdi:tooth-outline" style={{ fontSize: '1rem' }} />
            <span>Odontología</span>
          </Button>
        </div>
      ) : null}

      <div className="clinical-history-filters">
        <span className="clinical-history-filters__label">
          <i className="pi pi-filter" aria-hidden="true" />
          Filtrar por atención
        </span>
        <MultiSelect
          value={selectedServiceTypes}
          options={SERVICE_TYPE_OPTIONS}
          onChange={handleServiceTypeFilter}
          optionLabel="label"
          optionValue="value"
          itemTemplate={serviceTypeItemTemplate}
          placeholder="Todos los tipos"
          display="chip"
          showClear
          className="clinical-history-filters__select"
          maxSelectedLabels={3}
          selectedItemsLabel="{0} tipos seleccionados"
        />
        <span className="clinical-history-filters__count">
          {loading
            ? 'Actualizando…'
            : totalCount === 1
              ? '1 registro'
              : `${totalCount} registros`}
        </span>
        <Button
          icon="pi pi-refresh"
          className="p-button-text p-button-rounded clinical-history-filters__refresh"
          aria-label="Actualizar historia clínica"
          tooltip="Actualizar"
          tooltipOptions={{ position: 'top' }}
          onClick={handleReload}
          disabled={loading}
        />
      </div>

      <div className="clinical-history-grid-shell" ref={gridShellRef}>
        {loading ? (
          <div className="clinical-history-loading" role="status" aria-live="polite">
            <span className="clinical-history-loading__icon" aria-hidden="true">
              <i className="pi pi-spinner pi-spin" />
            </span>
            <strong>Cargando historia clínica…</strong>
            <small>Estamos consultando los registros del paciente.</small>
          </div>
        ) : (
          <>
            {/* Desktop: DataTable */}
            <div className="clinical-history-desktop">
              <DataTable
                value={items}
                scrollable
                scrollHeight="flex"
                tableStyle={{ minWidth: '980px' }}
                emptyMessage="No hay registros en la historia clínica."
                className="clinical-history-table"
              >
                <Column header="Fecha" body={dateBody} style={{ width: '175px' }} />
                <Column header="Atención" body={attentionBody} style={{ width: '250px' }} />
                <Column header="Profesional" body={responsibleBody} style={{ width: '225px' }} />
                <Column header="Motivo / detalle" body={reasonBody} />
                <Column header="Acciones" body={actionsBody} style={{ width: '105px' }} />
              </DataTable>
            </div>

            {/* Mobile: Card list */}
            <div className="clinical-history-mobile">
              {items.length === 0 ? (
                <div className="clinical-history-mobile__empty">No hay registros en la historia clínica.</div>
              ) : null}
              {items.map((item) => {
                const presentation = serviceTypePresentation(item.serviceTypeId);
                return (
                  <div key={`${item.healthPatientServiceId}-${item.patientControlId}`} className="ch-card">
                    <div className="ch-card__header">
                      <span className="ch-card__date">{formatDateTime(item.serviceDate)}</span>
                      <span className={`clinical-history-type clinical-history-type--${presentation.tone}`}>
                        <i className={`pi ${presentation.icon}`} aria-hidden="true" />
                        {presentation.label}
                      </span>
                    </div>
                    {item.activity && item.activity !== presentation.label ? (
                      <div className="ch-card__activity">{item.activity}</div>
                    ) : null}
                    {item.responsible ? (
                      <div className="ch-card__responsible">
                        <i className="pi pi-user" style={{ fontSize: '0.625rem' }} /> {item.responsible}
                      </div>
                    ) : null}
                    {item.reason ? (
                      <div className="ch-card__reason">{item.reason}</div>
                    ) : null}
                    <div className="ch-card__actions">{actionsBody(item)}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {loadingMore ? (
          <div className="clinical-history-loading-more">
            <i className="pi pi-spinner pi-spin" aria-hidden="true" />
            <span>Cargando más registros…</span>
          </div>
        ) : null}
      </div>
    </section>
    {patientId !== null ? (
      <HistoricalValuesPanel patientId={patientId} />
    ) : null}
    </div>
  );
}
